import React, { useMemo, useState } from 'react';
import { BlockPrintPattern } from '../brand/Jaipur';
import { Eyebrow, ReelTile, focusRing, getInitials, useJson } from './shared';
import { getVideoPoster } from '../../utils/media';

const MAX_MEMBERS = 16;

function LinkedInIcon({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
    </svg>
  );
}

/**
 * Behind-the-scenes clips from the studio. Renders nothing until clips are passed in
 * (shape: [{ src, poster?, title }]).
 */
function BtsClips({ clips }) {
  const [soundIdx, setSoundIdx] = useState(null);
  if (!Array.isArray(clips) || !clips.length) return null;
  return (
    <ul aria-label="Behind the scenes" className="home-scroller mt-8 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:px-6 lg:px-8">
      {clips.map((clip, i) => (
        <li key={clip.src || i} className="snap-start shrink-0">
          <ReelTile
            src={clip.src}
            poster={clip.poster || getVideoPoster(clip.src)}
            title={clip.title || 'Behind the scenes at Apnaaapan'}
            soundOn={soundIdx === i}
            onToggleSound={() => setSoundIdx((cur) => (cur === i ? null : i))}
            className="aspect-[9/16] h-72 rounded-3xl"
          />
        </li>
      ))}
    </ul>
  );
}

export default function MeetTheTeam({ btsClips = [] }) {
  const { data, error } = useJson('/api/team');

  const team = useMemo(() => {
    const list = data && Array.isArray(data.team) ? data.team : [];
    return list
      .filter((m) => m && m.name && (!m.status || m.status === 'published'))
      .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
      .slice(0, MAX_MEMBERS);
  }, [data]);

  if (error || (data && !team.length)) return null;

  return (
    <section aria-labelledby="home-team-title" className="relative overflow-hidden bg-apn-bg py-14 sm:py-20 short:py-10">
      <div className="text-apn-pink">
        <BlockPrintPattern opacity={0.06} />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr] lg:items-end">
          <div>
            <Eyebrow>The team</Eyebrow>
            <h2 id="home-team-title" data-animate="split-heading" className="mt-3 font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.03em] text-apn-ink sm:text-5xl lg:text-6xl">
              Real people. Real Jaipur studio.
            </h2>
          </div>
          <p className="text-lg text-apn-text lg:pb-2">
            Every reel, logo and landing page is made in our Jaipur studio by the people below. No outsourcing, no bots, no disappearing account managers.
          </p>
        </div>
      </div>

      <ul
        aria-label="Team members"
        className="home-scroller relative mt-8 flex snap-x gap-3 overflow-x-auto scroll-px-4 px-4 pb-2 sm:scroll-px-6 sm:px-6 lg:mx-auto lg:grid lg:max-w-7xl lg:grid-cols-6 lg:gap-4 lg:overflow-visible lg:px-8 xl:grid-cols-8"
      >
        {data
          ? team.map((m) => (
              <li key={m.id || m.name} data-animate-item className="w-36 shrink-0 snap-start sm:w-40 lg:w-auto">
                <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-apn-surface-2">
                  {m.image ? (
                    <img src={m.image} alt={`${m.name}, ${m.role}`} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  ) : (
                    <span aria-hidden="true" className="flex h-full w-full items-center justify-center font-display text-3xl font-extrabold text-apn-muted">
                      {getInitials(m.name)}
                    </span>
                  )}
                  {m.linkedin ? (
                    <a
                      href={m.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${m.name} on LinkedIn`}
                      className={`absolute bottom-2 right-2 flex h-11 w-11 items-center justify-center rounded-full bg-apn-surface text-apn-ink shadow transition hover:bg-apn-orange hover:text-white ${focusRing}`}
                    >
                      <LinkedInIcon />
                    </a>
                  ) : null}
                </div>
                <p className="mt-2 font-semibold leading-tight text-apn-ink">{m.name}</p>
                <p className="text-sm leading-snug text-apn-muted">{m.role}</p>
              </li>
            ))
          : Array.from({ length: 6 }).map((_, i) => (
              <li key={i} aria-hidden="true" className="w-36 shrink-0 sm:w-40 lg:w-auto">
                <div className="aspect-[4/5] animate-pulse rounded-2xl bg-apn-surface-2" />
                <div className="mt-2 h-4 w-24 rounded bg-apn-surface-2" />
              </li>
            ))}
      </ul>

      <BtsClips clips={btsClips} />
    </section>
  );
}
